import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSEO } from '@/hooks/useSEO';
import { buildChapters, getChapterPages, getComicById } from '@/utils/comic';
import ReaderSettings from './components/ReaderSettings';

const widthMap: Record<string, string> = {
  narrow: 'max-w-[680px]',
  normal: 'max-w-[820px]',
  wide: 'max-w-[1000px]',
};

const scaleMap: Record<string, string> = {
  small: 'w-[70%]',
  medium: 'w-[88%]',
  large: 'w-full',
};

const themeMap: Record<string, string> = {
  day: 'bg-background-50',
  sepia: 'bg-accent-50',
  night: 'bg-foreground-950',
};

export default function ReadPage() {
  const { comicId = '', chapterId = '' } = useParams();
  const navigate = useNavigate();
  const comic = getComicById(comicId);

  const [width, setWidth] = useState('normal');
  const [theme, setTheme] = useState('day');
  const [scale, setScale] = useState('medium');
  const [showSettings, setShowSettings] = useState(false);
  const [zoomed, setZoomed] = useState('');

  const chapterNum = Number(chapterId.replace('ch-', '')) || 0;
  const chapters = useMemo(() => (comic ? buildChapters(comic) : []), [comic]);
  const chapter = chapters.find((c) => c.id === chapterId);
  const valid = !!comic && !!chapter;

  useSEO(
    comic && chapter ? `${comic.title}-${chapter.title}-在线阅读` : '章节阅读-韩漫网址',
    comic && chapter
      ? `在线阅读《${comic.title}》${chapter.title}，支持纵向滚动阅读、夜间模式与阅读宽度调节，畅享舒适韩漫阅读体验。`
      : '韩漫漫画章节在线阅读。',
    comic ? `${comic.title},${comic.title}${chapter ? chapter.title : ''},韩漫在线阅读` : '韩漫阅读',
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [chapterId]);

  if (!valid || !comic || !chapter) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="mt-4 text-xl font-bold text-foreground-950">章节不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">该章节可能已被移除，或链接有误</p>
          <div className="mt-6">
            <Link
              to={comic ? `/detail/${comic!.id}` : '/'}
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              返回漫画详情
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const pages = getChapterPages(comic, chapterId);
  const prevId = chapterNum < comic.chapterCount ? `ch-${chapterNum + 1}` : '';
  const nextId = chapterNum > 1 ? `ch-${chapterNum - 1}` : '';
  const isNight = theme === 'night';

  const go = (id: string) => {
    if (id) navigate(`/read/${comic.id}/${id}`);
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-7xl px-4 pt-4 md:px-6">
        <nav className={`flex flex-wrap items-center gap-1 text-sm ${isNight ? 'text-background-300' : 'text-foreground-500'}`}>
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/detail/${comic.id}`} className="truncate transition-colors hover:text-primary-600">
            {comic.title}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className={isNight ? 'text-background-100' : 'text-foreground-800'}>{chapter.title}</span>
        </nav>
      </div>

      <div className="sticky top-16 z-40 mt-3 border-y border-background-200 bg-background-50">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3 md:px-6">
          <Link
            to={`/detail/${comic.id}`}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            详情页
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-bold text-foreground-950 md:text-base">
              {comic.title}
            </h1>
            <p className="truncate text-xs text-foreground-500">{chapter.title}</p>
          </div>
          <select
            value={chapterId}
            onChange={(e) => go(e.target.value)}
            className="max-w-[160px] rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 focus:outline-none focus:ring-1 focus:ring-primary-400 cursor-pointer"
          >
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowSettings((v) => !v)}
            className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-xs transition-colors cursor-pointer ${
              showSettings
                ? 'bg-primary-500 text-background-50'
                : 'border border-background-300 text-foreground-700 hover:bg-background-100'
            }`}
          >
            <i className="ri-settings-3-line" />
            阅读设置
          </button>
        </div>

        {showSettings && (
          <div className="border-t border-background-200 bg-background-100">
            <div className="mx-auto max-w-7xl px-4 py-4 md:px-6">
              <ReaderSettings
                width={width}
                theme={theme}
                scale={scale}
                onWidth={setWidth}
                onTheme={setTheme}
                onScale={setScale}
              />
            </div>
          </div>
        )}
      </div>

      <div className={themeMap[theme]}>
        <div className={`mx-auto ${widthMap[width]} px-2 py-6`}>
          {pages.map((src, i) => (
            <img
              key={`${chapterId}-${i}`}
              src={src}
              alt={`${comic.title} ${chapter.title} 第 ${i + 1} 页`}
              title={`${comic.title} ${chapter.title} 在线阅读`}
              loading="lazy"
              onClick={() => setZoomed(src)}
              className={`mx-auto mb-3 block cursor-zoom-in rounded-md ${scaleMap[scale]}`}
            />
          ))}

          <div className={`mt-8 rounded-lg border p-6 text-center ${isNight ? 'border-background-800 bg-background-900' : 'border-background-200 bg-background-50'}`}>
            <p className={`text-sm ${isNight ? 'text-background-300' : 'text-foreground-600'}`}>
              《{comic.title}》第{chapterNum}话 阅读完毕
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                disabled={!nextId}
                onClick={() => go(nextId)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-medium transition-colors ${
                  nextId
                    ? 'border-background-300 text-foreground-700 hover:bg-background-100 cursor-pointer'
                    : 'cursor-not-allowed border-background-200 text-foreground-300'
                }`}
              >
                <i className="ri-arrow-left-line" />
                上一章
              </button>
              <Link
                to={`/detail/${comic.id}`}
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                返回详情
              </Link>
              <button
                type="button"
                disabled={!prevId}
                onClick={() => go(prevId)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                  prevId
                    ? 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                    : 'cursor-not-allowed bg-background-200 text-foreground-400'
                }`}
              >
                下一章
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-40 border-t border-background-200 bg-background-50/95 backdrop-blur md:hidden">
        <div className="flex items-center justify-between px-4 py-2.5">
          <button
            type="button"
            disabled={!nextId}
            onClick={() => go(nextId)}
            className={`flex items-center gap-1 whitespace-nowrap rounded-md px-4 py-2 text-xs font-medium ${
              nextId ? 'bg-background-100 text-foreground-700' : 'text-foreground-300'
            }`}
          >
            <i className="ri-arrow-left-line" />
            上一章
          </button>
          <Link
            to={`/detail/${comic.id}`}
            className="flex items-center gap-1 whitespace-nowrap rounded-md bg-secondary-100 px-4 py-2 text-xs font-medium text-secondary-900"
          >
            <i className="ri-list-unordered" />
            目录
          </Link>
          <button
            type="button"
            disabled={!prevId}
            onClick={() => go(prevId)}
            className={`flex items-center gap-1 whitespace-nowrap rounded-md px-4 py-2 text-xs font-medium ${
              prevId ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-400'
            }`}
          >
            下一章
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>

      {zoomed && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/90 p-4 animate-fade-in"
          onClick={() => setZoomed('')}
        >
          <button
            type="button"
            className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/20 text-background-50"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={zoomed}
            alt={`${comic.title} ${chapter.title} 放大预览`}
            className="max-h-[90vh] max-w-full rounded-md object-contain"
          />
        </div>
      )}
    </SiteLayout>
  );
}